import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BrandModeProvider, useBrandMode } from "./BrandModeContext";
import { logout } from "../../store/actions/userActions";
import wordmark from "../../assets/brand/cache-wordmark.svg";
import iconMail from "../../assets/brand/icon-mail.svg";
import { initialsOf } from "./brandDealUtils";
import "./brandMode.css";
import { useFitScale, fitScaleStyle } from "./useFitScale";

// Brand Mode shell (Figma "AppShell / Brand"): 72px topbar with the CACHE
// wordmark, messages + unread badge, BRAND MODE label, user name and avatar
// menu; then the Home / Discover / Campaigns tab bar. It is the only header
// brand users see. An athlete's profile (/brand/athletes/:athleteId) and the
// account pages the avatar menu opens (/brand/profile, /brand/billing)
// belong to no tab, so none is active there; other pages without a tab keep
// Home active.

const TABS = [
  { path: "/brand/home", label: "Home", match: ["/brand/home"] },
  { path: "/brand/discover", label: "Discover", match: ["/brand/discover", "/brand/compare", "/brand/shortlist"] },
  { path: "/brand/campaigns", label: "Campaigns", match: ["/brand/campaigns", "/brand/deals", "/brand/messages"], badge: true },
];

// Pages under /brand that light up no tab.
const NO_TAB_PATHS = ["/brand/athletes", "/brand/profile", "/brand/billing"];
const onNoTabPage = (pathname) => NO_TAB_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const BRAND_PROFILE_PATH = "/brand/profile";
export const BRAND_BILLING_PATH = "/brand/billing";
const PROFILE_ROLES = ["IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD", "IS_ADMIN"];

const displayName = (user) => {
  const first = user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Brand";
};

const AvatarMenu = ({ user, permissions }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName(user);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("myEmail");
    localStorage.removeItem("athleteEmail");
    localStorage.removeItem("expiresAt");
    dispatch(logout());
    navigate("/login");
  };

  const isAdmin = permissions.includes("IS_ADMIN") || permissions.includes("IS_CACHE_AI");
  // The account pages keep the /business/profile and /business/billing
  // roles: Cache AI staff without a business role have no profile page, and
  // billing is for the accounts that pay for a brand plan.
  const hasProfile = PROFILE_ROLES.some((p) => permissions.includes(p));
  const hasBilling = permissions.includes("IS_BRAND_AGENCY");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-4"
      >
        <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>
        {user?.avatar ? (
          <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white">
            {initials}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="bm-fade-in absolute right-0 top-11 z-40 w-56 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl"
        >
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
          </div>
          <div className="border-t border-bm-border" />
          {isAdmin && (
            <Link to="/business/dashboard" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              Admin dashboard
            </Link>
          )}
          {hasProfile && (
            <Link to={BRAND_PROFILE_PATH} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              Profile
            </Link>
          )}
          {hasBilling && (
            <Link to={BRAND_BILLING_PATH} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              Billing
            </Link>
          )}
          <Link to="/brand/password" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Password
          </Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

const Shell = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user.user || {});
  const permissions = user.permissions || [];
  const { badges } = useBrandMode();

  const fit = useFitScale();

  const activeTab = TABS.find((t) => t.match.some((m) => location.pathname.startsWith(m))) || (onNoTabPage(location.pathname) ? null : TABS[0]);

  return (
    <div className="bm-root bg-white" style={fitScaleStyle(fit)}>
      {/* Topbar */}
      <header className="bg-white">
        <div className="flex h-[72px] w-full items-center justify-between px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
          <Link to="/brand/home" className="flex items-center" aria-label="Brand Mode home">
            <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/brand/messages" className="flex items-center gap-[6px]" aria-label="Messages">
              <img src={iconMail} alt="" className="h-5 w-5" />
              {badges.unread > 0 && (
                <span className="flex h-[22px] items-center justify-center rounded-full bg-bm-orange px-[9px] bm-label-xs text-white">
                  {badges.unread}
                </span>
              )}
            </Link>
            <span className="h-5 w-px bg-bm-border" />
            <span className="hidden bm-overline md:inline">Brand Mode</span>
            <AvatarMenu user={user} permissions={permissions} />
          </div>
        </div>
        {/* Tabbar */}
        <nav className="border-b border-bm-border">
          <div className="flex h-[51px] w-full items-stretch px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
            {TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={`flex items-center gap-2 px-6 font-archivo text-[15px] font-bold leading-5 ${
                    active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"
                  }`}
                >
                  {tab.label}
                  {tab.badge && badges.activeCampaigns > 0 && (
                    <span
                      className={`flex h-[22px] items-center justify-center rounded-full px-[9px] bm-label-xs ${
                        active ? "bg-bm-orange text-white" : "bg-bm-border text-bm-muted"
                      }`}
                    >
                      {badges.activeCampaigns}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="w-full px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
        <Outlet />
      </main>
    </div>
  );
};

const BrandModeLayout = () => (
  <BrandModeProvider>
    <Shell />
  </BrandModeProvider>
);

export default BrandModeLayout;
